import {
  Component,
  NgZone,
  OnInit,
  ViewChild,
  effect,
} from "@angular/core";
import { MatSidenav } from "@angular/material/sidenav";
import { DataService } from "./data.service";
import { AuthService } from "./auth.service";
import {
  fadeAnimation,
  flip,
  slideBazas,
  slideUpDown,
} from "./shared/fade.animation";
import { UntypedFormControl } from "@angular/forms";
import { interval } from "rxjs";
import { AppDB, switchDatabase, dbSwitching$ } from "./db.service";
import { SwUpdate } from "@angular/service-worker";
import { ToastrService } from "ngx-toastr";
import { environment } from "src/environments/environment";
import { SyncService } from "./sync.service";
import { PusherService } from "./pusher.service";
import { ConfirmationDialogComponent } from "./shared/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  animations: [fadeAnimation, slideUpDown, flip, slideBazas],
})
export class AppComponent implements OnInit {
  @ViewChild("sidenav") sidenav: MatSidenav;
  btnToggleNav: HTMLElement = document.getElementsByClassName(
    "btnClose"
  )[0] as HTMLElement;
  sideNavMode: string;
  matOpened = localStorage.hasOwnProperty("sideNavOpenState")
    ? localStorage.getItem("sideNavOpenState")
    : true;
  showReports: boolean = true;
  showSideNav: boolean = false;
  windowScrolled: boolean = false;
  appVersion = environment.version;
  liActive: number = 0;
  liClicked: number;
  direction: boolean = false;
  iconState = this.matOpened ? "flipped" : "default";
  uuid = uuidv4();
  
  // Track processing SYNC_SUCCESS messages to prevent race conditions
  private processingSyncMessages = new Map<string, Promise<void>>();

  constructor(
    public _dataService: DataService,
    public _syncService: SyncService,
    public _authService: AuthService,
    private _pusher: PusherService,
    private swUpdate: SwUpdate,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {
    window.addEventListener("focus", () => {
      this.checkForUpdate();
    });
    effect(
      () => {
        if (this._dataService.dateChangedSig()) {
          this._dataService.setActiveClassOnDates();
        }
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        if (
          this._authService.userSignal() &&
          this._authService.userSignal()?.token &&
          navigator.onLine
        ) {
          this._syncService.syncDB();
          this._syncService.getSettings();
        }
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        if (this._dataService.dbChanged()) {
          this.changeDB(this._dataService.dbChanged());
        }
      },
      { allowSignalWrites: true }
    );
  }

  async ngOnInit(): Promise<void> {
    this._dataService.uuid = this.uuid;
    // PUSHER
    var channel = this._pusher.init(this._dataService.dbTable);
    channel.bind("general", (data) => {
      if (data?.uuid !== this.uuid) {
        this._syncService.syncDB();
      }
    });
    channel.pusher.connection.bind("state_change", () => {
      if (channel.pusher.connection.state == "unavailable") {
        this._dataService.onlineSubject.next(false);
      } else if (channel.pusher.connection.state == "connected") {
        this._dataService.onlineSubject.next(true);
      }
    });
    // DEXIE DB
    if (!localStorage.hasOwnProperty("dbTable")) {
      localStorage.setItem(
        "dbTable",
        JSON.stringify(this._dataService.dbTable)
      );
      await switchDatabase(this._dataService.dbTable);
    }
    // CHECK DEVICE TYPE
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      // MOBILE
      this.sideNavMode = "over";
      this.matOpened = false;
    } else {
      // PC
      this.sideNavMode = "side";
    }

    if (this.swUpdate.isEnabled) {
      this.checkForUpdate();
      this.ngZone.runOutsideAngular(() =>
        interval(1000 * 60 * 30).subscribe(() => {
          this.checkForUpdate();
        })
      );
    }

    this._dataService.setActiveClassOnDates();

    // Periodic sync every 30 minutes
    interval(1800000).subscribe(() => {
      this._syncService.syncDB();
    });

    // Cleanup stale temporary records every hour
    interval(3600000).subscribe(async () => {
      const result = await this._syncService.cleanupStaleTemporaryRecords();
    });

    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener("message", async (event) => {
        const message = event.data;

        if (message?.type === "SYNC_SUCCESS") {
          await this.handleSyncSuccess(message.payload);
        }
        if (message?.type == "DELETE_SUCCESS") {
          this._pusher.postData("Entry Deleted").subscribe();
        }
      });

      navigator.serviceWorker.ready.then((reg) => {
        if ("sync" in reg) {
          (reg as any).sync.register("app-sync");
        }
      });
    }
  }

  /**
   * Handle SYNC_SUCCESS messages with proper locking to prevent race conditions
   */
  async handleSyncSuccess(data: any): Promise<void> {
    const operationKey = `${data.operation}-${data.tempTransactionId}`;
    
    // If already processing this operation, wait for it to complete
    if (this.processingSyncMessages.has(operationKey)) {
      console.log(`Already processing ${operationKey}, waiting...`);
      await this.processingSyncMessages.get(operationKey);
      return;
    }
    
    // Mark operation as being tracked by sync service
    this._syncService.pendingSyncOperations.add(operationKey);
    
    // Create processing promise
    const processingPromise = (async () => {
      const db = new AppDB(data.dbName);
      
      try {
        await db.open();
        
        // Route to appropriate handler
        switch (data.operation) {
          case "ADD_BANK_ORDER":
            await this.handleBankOrderSync(db, data);
            break;
          case "ADD_SELL":
            await this.handleSellSync(db, data);
            break;
          case "ADD_STOCK_ENTRY":
            await this.handleStockEntrySync(db, data);
            break;
          case "ADD_PRODUCTION":
            await this.handleProductionSync(db, data);
            break;
          case "ADD_RECONCILIATION":
            await this.handleReconciliationSync(db, data);
            break;
          default:
            console.warn(`Unknown sync operation: ${data.operation}`);
        }
      } catch (error) {
        console.error(`Error handling sync success for ${operationKey}:`, error);
      } finally {
        await db.close();
      }
    })();
    
    // Store the processing promise
    this.processingSyncMessages.set(operationKey, processingPromise);
    
    try {
      await processingPromise;
    } finally {
      // Cleanup
      this.processingSyncMessages.delete(operationKey);
      this._syncService.pendingSyncOperations.delete(operationKey);
    }
  }

  /**
   * Robust duplicate detection - checks both transaction and child records
   */
  async isDuplicateSync(db: AppDB, realTransactionId: any, realChildId: any, childTable: 'bank' | 'sells' | 'stock' | 'production' | 'reconciliations'): Promise<boolean> {
    const transaction = await db.transactions.get(realTransactionId);
    
    // Check if transaction exists and is already synced
    if (transaction && transaction.syncStatus === 'synced') {
      // Double-check child record
      const childRecord = await db[childTable].get(realChildId);
      if (childRecord && (childRecord.syncStatus === 'synced' || childRecord.tId === realTransactionId)) {
        console.log(`Duplicate detected: Transaction ${realTransactionId} and ${childTable} ${realChildId} already synced`);
        return true;
      }
    }
    
    return false;
  }

  /**
   * Handle ADD_BANK_ORDER sync
   */
  async handleBankOrderSync(db: AppDB, data: any): Promise<void> {
    // Check for duplicates
    if (await this.isDuplicateSync(db, data.realTransactionId, data.realBankId, 'bank')) {
      return;
    }

    let transactionEntry = await db.transactions.get(data.tempTransactionId);
    if (!transactionEntry) {
      console.error("Transaction entry not found:", data.tempTransactionId);
      return;
    }
    
    transactionEntry = {
      ...transactionEntry,
      id: data.realTransactionId,
      syncStatus: "synced",
    };
    
    let bankEntry = await db.bank.get(data.tempBankId);
    if (!bankEntry) {
      console.error("Bank entry not found:", data.tempBankId);
      return;
    }
    
    bankEntry = {
      ...bankEntry,
      id: data.realBankId,
      tId: data.realTransactionId,
      transaction: transactionEntry,
      syncStatus: "synced",
    };

    await db.transaction("rw", db.transactions, db.bank, async () => {
      // Delete temp entries
      await db.transactions.delete(data.tempTransactionId);
      await db.bank.delete(data.tempBankId);

      // Use put instead of add to handle duplicates
      await db.transactions.put(transactionEntry);
      await db.bank.put(bankEntry);

      if (data.tempEdvId && data.realEdvId) {
        let edvEntry = await db.bank.get(data.tempEdvId);
        if (edvEntry) {
          edvEntry = {
            ...edvEntry,
            id: data.realEdvId,
            tId: data.realTransactionId,
            transaction: transactionEntry,
            syncStatus: "synced",
          };
          await db.bank.delete(data.tempEdvId);
          await db.bank.put(edvEntry);
        }
      }
    });

    this._pusher.postData(data).subscribe();
    console.log("Bank order sync completed successfully");
  }

  /**
   * Handle ADD_SELL sync
   */
  async handleSellSync(db: AppDB, data: any): Promise<void> {
    // Check for duplicates
    if (data.realSellIds && data.realSellIds.length > 0) {
      if (await this.isDuplicateSync(db, data.realTransactionId, data.realSellIds[0], 'sells')) {
        return;
      }
    }

    await db.transaction(
      "rw",
      db.transactions,
      db.sells,
      db.bank,
      async () => {
        let transactionEntry = await db.transactions.get(data.tempTransactionId);
        if (!transactionEntry) {
          console.error("Transaction entry not found:", data.tempTransactionId);
          return;
        }
        
        let sellEntries = await db.sells.where({ tId: data.tempTransactionId }).toArray();

        // Delete the old local entries
        await db.transactions.delete(data.tempTransactionId);
        await db.sells.where({ tId: data.tempTransactionId }).delete();

        // Update entries with real IDs and "synced" status
        transactionEntry.id = data.realTransactionId;
        transactionEntry.syncStatus = "synced";

        sellEntries.forEach((sell, index) => {
          sell.id = data.realSellIds[index];
          sell.tId = data.realTransactionId;
          sell.syncStatus = "synced";
        });

        // Add the updated entries back to the database
        await db.transactions.put(transactionEntry);
        await db.sells.bulkPut(sellEntries);

        // Handle the optional bank entry
        if (data.tempBankId && data.realBankId) {
          let bankEntry = await db.bank.get(data.tempBankId);
          if (bankEntry) {
            await db.bank.delete(data.tempBankId);
            bankEntry.id = data.realBankId;
            bankEntry.tId = data.realTransactionId;
            bankEntry.syncStatus = "synced";
            await db.bank.put(bankEntry);
          }
        }
      }
    );

    this._pusher.postData({
      transaction: data.realTransactionId,
      sells: data.realSellIds,
    }).subscribe();
    console.log("Sell sync completed successfully");
  }

  /**
   * Handle ADD_STOCK_ENTRY sync
   */
  async handleStockEntrySync(db: AppDB, data: any): Promise<void> {
    // Check for duplicates
    if (data.realStockIds && data.realStockIds.length > 0) {
      if (await this.isDuplicateSync(db, data.realTransactionId, data.realStockIds[0], 'stock')) {
        return;
      }
    }

    await db.transaction(
      "rw",
      db.transactions,
      db.stock,
      db.bank,
      async () => {
        let transactionEntry = await db.transactions.get(data.tempTransactionId);
        if (!transactionEntry) {
          console.error("Transaction entry not found:", data.tempTransactionId);
          return;
        }
        
        let stockEntries = await db.stock.where({ tId: data.tempTransactionId }).toArray();

        // Delete the old local entries
        await db.transactions.delete(data.tempTransactionId);
        await db.stock.where({ tId: data.tempTransactionId }).delete();

        // Update entries with real IDs and "synced" status
        transactionEntry.id = data.realTransactionId;
        transactionEntry.syncStatus = "synced";

        stockEntries.forEach((stock, index) => {
          stock.id = data.realStockIds[index];
          stock.tId = data.realTransactionId;
          stock.syncStatus = "synced";
        });

        // Add the updated entries back
        await db.transactions.put(transactionEntry);
        await db.stock.bulkPut(stockEntries);

        // Handle the optional bank entry
        if (data.tempBankId && data.realBankId) {
          let bankEntry = await db.bank.get(data.tempBankId);
          if (bankEntry) {
            await db.bank.delete(data.tempBankId);
            bankEntry.id = data.realBankId;
            bankEntry.tId = data.realTransactionId;
            bankEntry.syncStatus = "synced";
            await db.bank.put(bankEntry);
          }
        }
      }
    );

    this._pusher?.postData("New Stock Entry").subscribe();
    console.log("Stock entry sync completed successfully");
  }

  /**
   * Handle ADD_PRODUCTION sync
   */
  async handleProductionSync(db: AppDB, data: any): Promise<void> {
    // Check for duplicates
    if (data.realProductionId) {
      if (await this.isDuplicateSync(db, data.realTransactionId, data.realProductionId, 'production')) {
        return;
      }
    }

    await db.transaction(
      "rw",
      db.transactions,
      db.production,
      db.stock,
      async () => {
        let transactionEntry = await db.transactions.get(data.tempTransactionId);
        if (!transactionEntry) {
          console.error("Transaction entry not found:", data.tempTransactionId);
          return;
        }
        
        let productionEntry = await db.production.get(data.tempProductionId);
        let usedProducts = await db.production
          .where("id")
          .anyOf(data.tempUsedProductIds || [])
          .toArray();
        let stockEntries = await db.stock.where({ tId: data.tempTransactionId }).toArray();

        // Delete old entries
        await db.transactions.delete(data.tempTransactionId);
        await db.production.delete(data.tempProductionId);
        if (data.tempUsedProductIds && data.tempUsedProductIds.length > 0) {
          await db.production.bulkDelete(data.tempUsedProductIds);
        }
        await db.stock.where({ tId: data.tempTransactionId }).delete();

        // Update with real IDs
        transactionEntry.id = data.realTransactionId;
        transactionEntry.syncStatus = "synced";

        if (productionEntry) {
          productionEntry.id = data.realProductionId;
          productionEntry.tId = data.realTransactionId;
          productionEntry.syncStatus = "synced";
        }

        usedProducts.forEach((prod, index) => {
          prod.id = data.realUsedProductIds[index];
          prod.tId = data.realTransactionId;
          prod.syncStatus = "synced";
        });

        stockEntries.forEach((stock, index) => {
          stock.id = data.realStockIds[index];
          stock.tId = data.realTransactionId;
          stock.syncStatus = "synced";
        });

        // Add updated entries
        await db.transactions.put(transactionEntry);
        if (productionEntry) {
          await db.production.put(productionEntry);
        }
        if (usedProducts.length > 0) {
          await db.production.bulkPut(usedProducts);
        }
        if (stockEntries.length > 0) {
          await db.stock.bulkPut(stockEntries);
        }
      }
    );

    this._pusher?.postData("New Production").subscribe();
    console.log("Production sync completed successfully");
  }

  /**
   * Handle ADD_RECONCILIATION sync
   */
  async handleReconciliationSync(db: AppDB, data: any): Promise<void> {
    // Check for duplicates
    if (data.realReconIds && data.realReconIds.length > 0) {
      if (await this.isDuplicateSync(db, data.realTransactionId, data.realReconIds[0], 'reconciliations')) {
        console.log("Duplicate reconciliation sync detected, skipping");
        return;
      }
    }

    await db.transaction(
      "rw",
      db.transactions,
      db.stock,
      db.reconciliations,
      async () => {
        // 1. Update Transaction
        let transactionEntry = await db.transactions.get(data.tempTransactionId);
        if (transactionEntry) {
          await db.transactions.delete(data.tempTransactionId);
          transactionEntry = {
            ...transactionEntry,
            id: data.realTransactionId,
            syncStatus: "synced",
          };
          await db.transactions.put(transactionEntry);
        }

        // 2. Update Stock Entries
        if (data.tempStockIds && data.realStockIds) {
          for (let i = 0; i < data.tempStockIds.length; i++) {
            const tempId = data.tempStockIds[i];
            const realId = data.realStockIds[i];
            const entry = await db.stock.get(tempId);
            if (entry) {
              await db.stock.delete(tempId);
              await db.stock.put({
                ...entry,
                id: realId,
                tId: data.realTransactionId,
                transactionId: data.realTransactionId,
                syncStatus: "synced",
              });
            }
          }
        }

        // 3. Update Reconciliation Entries
        if (data.tempReconIds && data.realReconIds) {
          for (let i = 0; i < data.tempReconIds.length; i++) {
            const tempId = data.tempReconIds[i];
            const realId = data.realReconIds[i];
            const entry = await db.reconciliations.get(tempId);
            if (entry) {
              await db.reconciliations.delete(tempId);
              await db.reconciliations.put({
                ...entry,
                id: realId,
                tId: data.realTransactionId,
                transactionId: data.realTransactionId,
                syncStatus: "synced",
              });
            }
          }
        }
      }
    );

    this._pusher?.postData("New Reconciliation").subscribe();
    this._dataService.dateChanged.next(new Date().toISOString());
    console.log("Reconciliation sync completed successfully");
  }

  toggleSidenav(event?, state?) {
    // ON PC STORE SIDENAV OPEN/CLOSE STATE
    if (state != null && !/Mobi|Android/i.test(navigator.userAgent)) {
      localStorage.setItem("sideNavOpenState", state);
      this._dataService.showSideNav = state;
    }
    // ON MOBILE CLOSE SIDENAV ON NAV ITEM CLICKS
    if (/Mobi|Android/i.test(navigator.userAgent) && state == null) {
      this.sidenav.close();
    }
  }

  setDateRange(
    startDate: moment.Moment,
    endDate: moment.Moment,
    clickedRange: any
  ) {
    this._dataService.dateRanges.forEach((range) => {
      range.active = range === clickedRange;
    });
    this._dataService.startDate = new UntypedFormControl(startDate);
    this._dataService.setLS("startDate", this._dataService.startDate);
    this._dataService.endDate = new UntypedFormControl(endDate);
    this._dataService.setLS("endDate", this._dataService.endDate);
    this._dataService.dateChanged.next(moment().startOf("month").toString());
    this._dataService.dateChangedSig.set(
      this._dataService.startDate.value.format("DD-MM-YYYY") +
        this._dataService.endDate.value.format("DD-MM-YYYY")
    );
  }

  reset() {
    const data = {
      header: "Sıfırla",
      body: "Verilənlər bazası sıfırlanıb yenidən sinxronizə ediləcək",
      footer: "Davam et?",
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "500px",
      data: data,
      closeOnNavigation: true,
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this._syncService.reset();
      }
    });
  }

  onScroll(event) {
    this.windowScrolled = event.target.scrollTop > 100 ? true : false;
  }

  scrollTop() {
    setTimeout(() => {
      document.querySelector(".mat-sidenav-content").scrollTo(0, 0);
    }, 250);
  }

  scrollTopSmooth() {
    document.querySelector(".mat-sidenav-content, table").scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  checkForUpdate() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().then((res) => {
        if (res) {
          this.toastr
            .info(
              'Zəhmət olmasa proqramı yeniləyin <br/><br/> <a class="btn btn-block" click="reloadPage()">Yenilə</a>',
              "Yeni versiya mövcuddur!",
              {
                disableTimeOut: true,
                closeButton: true,
                enableHtml: true,
                toastClass: "ngx-toastr toast-info bg-toastr",
                tapToDismiss: false,
              }
            )
            .onTap.pipe()
            .subscribe(() => window.location.reload());
        }
      });
    }
  }

  // ANIMATE ROUTES
  public getRouterOutletState(outlet) {
    return outlet.isActivated ? outlet.activatedRouteData["tab"] : "";
  }

  async changeDB(database?: string, byUser?: boolean) {
    if (database) {
      this._syncService.showSyncLine = true;
      this._dataService.dbTable = database;
      localStorage.setItem("dbTable", JSON.stringify(database));
      
      // Wait for database switch to complete
      await switchDatabase(database);
      
      if (byUser) {
        window.location.reload();
        return; // Exit early if reloading
      }
      
      this._syncService.syncDate = this._dataService.getLS("syncDate");
      this._syncService.getLSData();
      
      if (navigator.onLine) {
        // Small delay to ensure database is fully ready
        await new Promise(resolve => setTimeout(resolve, 100));
        await this._syncService.syncDB();
        await this._syncService.getSettings();
      }
      
      this._dataService.dbName.set(
        this._dataService.database.find((db) => db.db === database)?.name
      );
      this._dataService.dbChanged.set(database);
      await this._syncService.cleanupOrphanedRecords();
    }
  }

  // HAMMER SWIPE CONTROLS
  // swipeForMobile(event) {
  //   if (/Mobi|Android/i.test(navigator.userAgent) &&
  //     !event.target.className.includes("mat-header") &&
  //     !event.target.className.includes("mat-cell") &&
  //     !event.target.className.includes("mat-sort") &&
  //     !event.target.closest('.noHammerSwipe')
  //   ) {
  //     this.sidenav?.open();
  //   }
  // }
}
